import React, { useEffect, useState } from 'react';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useDataSourceProfile from '@afosto/utils-react/dataSource/useDataSourceProfile';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import useSessionManager from '@afosto/auth-react/useSessionManager';
import { useHistory } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import InvoiceCard, { InvoiceCardSkeleton } from '../../components/InvoiceCard';
import InvoicePreview from './InvoicePreview';
import PaymentStatusDrawer from '../../components/PaymentStatusDrawer';
import { DataGrid } from '@afosto/app-components-react/DataGrid';
import DataGridPreview from '@afosto/app-components-react/DataGrid/DataGridPreview';
import DataList from '@afosto/app-components-react/DataList';
import Page from '@afosto/app-components-react/Page';
import createDataGridProfileView from '@afosto/utils-react/datagrid/createDataGridProfileView';
import createDefaultView from '@afosto/app-components-react/DataSource/modules/views/utils';
import filterOptionsQuery from '@afosto/utils-react/datagrid/filterOptionsQuery';
import searchQuery from '@afosto/utils-react/datagrid/searchQuery';
import updateDataGridDefaultView from '@afosto/utils-react/datagrid/updateDataGridDefaultView';
import updateDataGridProfilePositions from '@afosto/utils-react/datagrid/updateDataGridProfilePositions';
import updateDataGridProfileView from '@afosto/utils-react/datagrid/updateDataGridProfileView';
import { invoices as invoicesApi } from '@afosto/order/api/Invoices';
import {
  clientFormatter,
  contactFormatter,
  invoiceFormatter,
  organisationFormatter,
} from './formatters';
import translations from './translations';

const INVOICES_PROFILE_CODE = 'odr';
const INVOICES_PROFILE_MODEL = 'invoices';
const INVOICES_PROFILE_PATH = 'odr.invoices';
const INVOICES_PROFILE_NAMESPACE = 'default';

const InvoicesContainer = props => <Grid container spacing={2} {...props} />;

const Invoices = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:invoices:read']);

  const intl = useIntl();
  const history = useHistory();
  const [invoice, setInvoice] = useState(null);
  const [showPaymentStatusDrawer, setShowPaymentStatusDrawer] = useState(false);
  const sessionManager = useSessionManager();
  const searchIdentifier = {
    code: INVOICES_PROFILE_CODE,
    model: INVOICES_PROFILE_MODEL,
    namespace: INVOICES_PROFILE_NAMESPACE,
  };
  const fixedProperties = [
    {
      key: 'number',
      name: intl.formatMessage({
        id: `dataGrid.columns.${INVOICES_PROFILE_MODEL}.number`,
        defaultMessage: 'Invoice number',
        description: 'The column name for column number',
      }),
      type: 'invoice',
      isSortable: true,
    },
  ];
  const defaultProperties = [
    {
      key: 'contact_id',
      name: intl.formatMessage({
        id: `dataGrid.columns.${INVOICES_PROFILE_MODEL}.contact`,
        defaultMessage: 'Contact',
        description: 'The column name for column contact id',
      }),
      type: 'contact',
      isSortable: false,
    },
    {
      key: 'organisation_id',
      name: intl.formatMessage({
        id: `dataGrid.columns.${INVOICES_PROFILE_MODEL}.organisation`,
        defaultMessage: 'Organisation',
        description: 'The column name for column contact id',
      }),
      type: 'organisation',
      isSortable: false,
    },
    {
      key: 'total',
      name: intl.formatMessage({
        id: `dataGrid.columns.${INVOICES_PROFILE_MODEL}.total`,
        defaultMessage: 'Total',
        description: 'The column name for column total',
      }),
      type: 'currency',
    },
    {
      key: 'is_including_vat',
      name: intl.formatMessage({
        id: `dataGrid.columns.${INVOICES_PROFILE_MODEL}.isIncludingVat`,
        defaultMessage: 'Including VAT',
        description: 'The column name for column is including vat',
      }),
      type: 'boolean',
      isSortable: false,
    },
    {
      key: 'is_paid',
      name: intl.formatMessage({
        id: `dataGrid.columns.${INVOICES_PROFILE_MODEL}.isPaid`,
        defaultMessage: 'Paid',
        description: 'The column name for column is paid',
      }),
      type: 'boolean',
      isSortable: false,
    },
    {
      key: 'client_id',
      name: intl.formatMessage({
        id: `dataGrid.columns.${INVOICES_PROFILE_MODEL}.client`,
        defaultMessage: 'Client',
        description: 'The column name for column client id',
      }),
      type: 'client',
      isSortable: false,
    },
    {
      key: 'created_at',
      name: intl.formatMessage({
        id: `dataGrid.columns.${INVOICES_PROFILE_MODEL}.createdAt`,
        defaultMessage: 'Created at',
        description: 'The column name for column number',
      }),
      type: 'datetime',
    },
  ];
  const { isFetchingProfile, filters, views, properties: filterProperties } = useDataSourceProfile({
    ...searchIdentifier,
    filterTypes: {
      client_id: 'client',
      contact_id: 'contact',
      organisation_id: 'organisation',
      sku: 'sku',
      subtotal: 'currency',
      total: 'currency',
    },
    ignoreProperties: [
      'reference',
      'description',
      ...fixedProperties.map(property => property.key),
      ...defaultProperties.map(property => property.key),
    ],
    propertyTypes: {
      paid_at: 'date',
      subtotal: 'currency',
    },
  });

  const dataSource = useDataGrid(searchQuery(searchIdentifier), {
    gridOptions: {
      columnSelection: true,
      filterSelection: true,
      copyGridUrl: true,
      advancedSort: true,
      viewsSelection: true,
    },
    options: {
      dataSourceUrl: true,
      isLoadingData: isFetchingProfile,
      storeStateKey: 'invoices',
    },
    modules: [
      'dataProperties',
      'pagination',
      'activeItem',
      'filters',
      'sorting',
      'views',
      'search',
    ],
    views: {
      canCreateViewForTenant:
        sessionManager && sessionManager.hasPermissions(['odr:invoices:create']),
      defaultView: createDefaultView(intl, {
        properties: [...defaultProperties],
        sort: [
          {
            key: 'date_created',
            direction: 'desc',
          },
        ],
      }),
      views,
      viewShortcuts: true,
      onAddView: (view, otherViews) =>
        createDataGridProfileView(INVOICES_PROFILE_PATH)(view, { views: otherViews }),
      onUpdateView: view => updateDataGridProfileView(INVOICES_PROFILE_PATH)(view),
      onDeleteView: view => updateDataGridProfileView(INVOICES_PROFILE_PATH)(view),
      onRepositionView: repositionedViews =>
        updateDataGridProfilePositions(INVOICES_PROFILE_PATH)(repositionedViews),
      onChangeDefaultView: updatedViews =>
        updateDataGridDefaultView(INVOICES_PROFILE_PATH)(updatedViews),
    },
    dataProperties: {
      properties: [...defaultProperties, ...filterProperties, ...fixedProperties],
      fixedProperties,
    },
    filters: {
      filters,
      loadFilterOptions: filterOptionsQuery(searchIdentifier),
    },
  });
  const { activeItem: activeItemModule = {} } = dataSource;
  const { activeItem } = activeItemModule;

  const handleChangePaymentStatus = paidAt => {
    setShowPaymentStatusDrawer(false);
    setInvoice({ ...invoice, is_paid: true, paid_at: paidAt });
    dataSource.reloadDataSource();
  };

  /**
   * Fetch the invoice data if a invoice row is selected and show the data in the preview.
   */
  useEffect(() => {
    let isMounted = true;

    const fetchInvoice = async id => {
      try {
        const response = await invoicesApi.viewInvoice(id);
        const { data = {} } = response;

        if (isMounted) {
          setInvoice(data);
        }
      } catch (error) {
        if (isMounted) {
          setInvoice(null);
        }
      }
    };

    if (activeItem) {
      fetchInvoice(activeItem);
    } else {
      setInvoice(null);
    }

    return () => {
      isMounted = false;
    };
  }, [activeItem]);

  const previewMenu = [
    ...(invoice
      ? [
          {
            key: 'view_invoice',
            label: intl.formatMessage(translations.previewActions.view.label),
            icon: 'eye',
            shortcut: true,
            onClick: () => history.push(`/invoices/${invoice.id}`),
          },
        ]
      : []),
    ...(!invoice?.is_paid
      ? [
          {
            key: 'edit_payment_status_invoice',
            label: intl.formatMessage(translations.previewActions.changePaymentStatus.label),
            onClick: () => setShowPaymentStatusDrawer(true),
          },
        ]
      : []),
  ];

  const renderInvoice = invoiceData => <InvoiceCard invoice={invoiceData} key={invoiceData?.id} />;

  const renderInvoiceSkeleton = key => <InvoiceCardSkeleton key={key} />;

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  return (
    <Grid container wrap="nowrap">
      <Page
        title={intl.formatMessage(translations.title)}
        primaryAction={
          hasPermissions(['odr:invoices:create'])
            ? {
                text: intl.formatMessage(translations.create.button),
                action: () => history.push('/invoices/new'),
              }
            : null
        }
        style={{ flexGrow: 1 }}
        fullWidth
      >
        <Hidden implementation="js" mdUp>
          <DataList
            dataSource={dataSource}
            renderItem={renderInvoice}
            renderSkeletonItem={renderInvoiceSkeleton}
            component={InvoicesContainer}
          />
        </Hidden>
        <Hidden implementation="js" smDown>
          <DataGrid
            dataSource={dataSource}
            formatters={{
              invoice: invoiceFormatter,
              client: clientFormatter,
              contact: contactFormatter,
              organisation: organisationFormatter,
            }}
          />
        </Hidden>
      </Page>
      <DataGridPreview
        title={invoice?.number ?? 'Concept'}
        open={dataSource.activeItem.activeItem !== null}
        onClose={() => dataSource.activeItem.setActiveItem(null)}
        menu={previewMenu}
      >
        {invoice && <InvoicePreview invoice={invoice} />}
      </DataGridPreview>
      <PaymentStatusDrawer
        invoice={invoice}
        isOpen={showPaymentStatusDrawer}
        onChange={handleChangePaymentStatus}
        onClose={() => setShowPaymentStatusDrawer(false)}
      />
    </Grid>
  );
};

export default Invoices;
